import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import ModalGroup from "../../Shared/Components/ModalGroup"
import Header from "../../Shared/Header/Header"
import Footer from "../../Shared/Layout/Footer"
import RoomHeader from "../Components/RoomHeader"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { setRoomData, setRoomManagerData, setRoomModeratorData } from "../../Shared/SharedSlices/RoomSlice"
import { RootState } from "../../../App/store"
import { getRoomRequest } from "../RoomServiceRequests/RoomServiceRequests"
import { JoinStatus, RoleType, RoomType, StorageKey } from "../../../App/enums"
import { getRoomPostsRequest } from "../RoomPost/RoomPostRequests"
import { setPostStatus, setRoomPostsData } from "../RoomPost/RoomPostSlice"
import { capitalizeFirstLetter } from "../../../App/Helpers/Helpers"
import { headers } from "../../../App/Axios"
import HTTPService from "../../../App/HTTPService"
import SessionService from "../../../App/SessionService"
import { setIsMember, setPendingType } from "../RoomSlices/MemberInRoomStatusSlice"
import BackgroundWrapper from "../../Shared/Components/BackgroundWrapper"
import RoomInformation from "./RoomInformation"
import RoomPost from "../RoomPost/RoomPost"
import { LoadingAnimation, PostsTabIcon, MembersTabIcon, MediaTabIcon, EventsTabIcon, PostsCommentsTabIcon, EdusiiaSmallIcon } from "../../Shared/Components/SvgIcons"
import { hasPostDropdown } from "../RoomPost/hasPostDropdown"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import RoomMembers from "../Components/RoomMembers"
import MediaLibrary from "./MediaLibrary"
import RoomEvents from "./RoomEvents"
import RoomPostTab from "./RoomPostTab/RoomPostTab";
import { onJoinRoomRequest } from "../RoomServiceRequests/JoinRoomServiceRequest"
import { HapticFeedback } from "../../../App/Helpers/HapticFeedback"
import { useScrollToTopOnTab } from "../../../App/Helpers/useScrollToTopOnTab"
import MobileBottomNav from "../../Shared/Layout/MobileBottomNav";

function RoomScreen() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { roomId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const tabParam = searchParams.get('tab') || 'posts'
    const searchParamsString = searchParams.toString()
    const [pageNum, setPageNum] = useState(1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [loadingPost, setLoadingPost] = useState(false)
    const translation = useSelector(selectTranslations)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const organizationDataState = useSelector((state: RootState) => state.organizationsSlice.organizationSlice.organization)
    const roomDataState = useSelector((state: RootState) => state.roomSlice.roomSlice.room)
    const isRoomManager = useSelector((state: RootState) => state.roomSlice.roomManagerSlice.room_manager)
    const isRoomModerator = useSelector((state: RootState) => state.roomSlice.RoomModeratorSlice.room_moderator)
    const posts = useSelector((state: RootState) => state.RoomPostsSlice.roomPostsSlice.posts)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const isMemberInRoom = useSelector((state: RootState) => state.MemberInRoomStatusSlice.MemberInRoomStatusSlice.isMember)
    const allow_requests = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.allow_requests!
    const invite_others = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.invite_others!
    const share_documents = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.share_documents!
    const only_admins_can_upload = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.only_admins_can_upload!
    const canSeePosts = roomDataState.type === RoomType.Private ? isMemberInRoom === JoinStatus.MEMBER : true
    const scrollContainer = useRef<HTMLDivElement>(null)
    const postStatus = useSelector((state: RootState) => state.RoomPostsSlice.postStatusSlice.postsStatus)
    const [isLoading, setIsLoading] = useState(true)
    const tabIndex =
        tabParam === 'members'
            ? 1
            : tabParam === 'new-post'
                ? 2
                : tabParam === 'media'
                    ? 3
                    : tabParam === 'events'
                        ? 4
                        : 0

    const hasPrivelegedPermission = isRoomManager || isRoomModerator
    const shouldAutoJoinFromPreview = searchParams.get("previewJoin") === "1"

    // Scroll-to-top on tab tap
    const { handleTabSelect: handleScrollToTop, registerScrollContainer } = useScrollToTopOnTab(tabIndex)

    useEffect(() => {
        const updated = new URLSearchParams(searchParamsString)
        switch (tabParam) {
            case 'posts':
                updated.delete('file')
                updated.delete('event')
                break
            case 'members':
                updated.delete('file')
                updated.delete('event')
                break
            case 'new-post':
                updated.delete('file')
                break
            case 'media':
                updated.delete('event')
                break
            case 'events':
                updated.delete('file')
                break
            default:
                updated.delete('file')
                updated.delete('event')
        }

        if (updated.toString() !== searchParamsString) {
            setSearchParams(updated, { replace: true })
        }
    }, [tabParam, searchParamsString, setSearchParams])

    useEffect(() => {
        if (undefined !== roomDataState.id && parseInt(roomId!) === roomDataState.id) {
            setIsLoading(false)
        }
    }, [roomId, roomDataState])

    useEffect(() => {
        let isMounted = true
        getRoomRequest(parseInt(roomId!))
            .then(response => {
                if (isMounted && response.status === 200) {
                    dispatch(setRoomData(response.data))
                }
            })
            .catch(error => {
                if (error.response.status === 404) {
                    navigate("/not-found")
                }
            })
    }, [
        dispatch,
        roomId,
        roomDataState.name,
        roomDataState.photo,
        roomDataState.type,
        allow_requests,
        only_admins_can_upload,
        invite_others,
        share_documents,
        roomDataState.description,
        navigate,
    ])

    const getUserStatus = useCallback(() => {
        return HTTPService.get(`/user-status-in-room/${roomId}/`, headers)
    }, [roomId])

    useEffect(() => {
        getUserStatus().then(response => {
            if (response.status === 200) {
                dispatch(setIsMember(response.data.status))
                dispatch(setPendingType({ type: response.data.type, notification_id: response.data.notification_id }))
            }
        })
    }, [dispatch, getUserStatus])

    useEffect(() => {
        if (!shouldAutoJoinFromPreview || !roomId) {
            return
        }

        const hasKnownMembershipState = [JoinStatus.NON_MEMBER, JoinStatus.MEMBER, JoinStatus.PENDING].includes(isMemberInRoom as JoinStatus)
        if (!hasKnownMembershipState) {
            return
        }

        if (role === RoleType.USER && isMemberInRoom === JoinStatus.NON_MEMBER && roomDataState.type) {
            dispatch<any>(onJoinRoomRequest(parseInt(roomId), roomDataState.type))
        }

        const nextParams = new URLSearchParams(searchParamsString)
        nextParams.delete("previewJoin")
        setSearchParams(nextParams, { replace: true })
        SessionService.removeItem(StorageKey.previewRoomJoinId)
    }, [dispatch, isMemberInRoom, role, roomDataState.type, roomId, searchParamsString, setSearchParams, shouldAutoJoinFromPreview])

    useEffect(() => {
        dispatch(setRoomManagerData(roomDataState?.room_managers?.some(manager => manager.id === userDataState.id)!))
    }, [dispatch, roomDataState?.room_managers, roomId, userDataState.id])

    useEffect(() => {
        dispatch(setRoomModeratorData(roomDataState?.room_moderators?.some(moderator => moderator.id === userDataState.id && moderator.type === 'user')!))
    }, [dispatch, roomDataState?.room_moderators, roomId, userDataState.id])

    useEffect(() => {
        dispatch(setPostStatus({ postsStatus: true, singularPostStatus: false, postedInRoomStatus: false }))
    }, [dispatch])

    useEffect(() => {
        let filtered = posts.filter(post => post.room === parseInt(roomId!))
        dispatch(setRoomPostsData(filtered))
    }, [roomId])

    useEffect(() => {
        const controller = new AbortController()
        const { signal } = controller
        if (postStatus) {
            setPageNum(1)
            getRoomPostsRequest(parseInt(roomId!), 1)
                .then(response => {
                    if (response.status === 200) {
                        dispatch(setRoomPostsData(response.data.results))
                        setHasNextPage(Boolean(response.data.next))
                        setLoadingPost(false)
                        dispatch(setPostStatus({ postsStatus: false, singularPostStatus: false, postedInRoomStatus: false }))
                    }
                })
                .catch(error => {
                    if (signal.aborted) return
                    console.log(error)
                })

            return () => {
                setPageNum(1)
                controller.abort()
            }
        }
    }, [dispatch, postStatus, roomId])

    const onFABClick = () => {
        HapticFeedback.medium()
        updateSearchParamsForTab(2)
    }

    const updateSearchParamsForTab = (index: number) => {
        const nextTab =
            index === 1
                ? 'members'
                : index === 2
                    ? 'new-post'
                    : index === 3
                        ? 'media'
                        : index === 4
                            ? 'events'
                            : 'posts'

        console.log(searchParamsString)
        const updated = new URLSearchParams(searchParamsString)
        updated.set('tab', nextTab)
        if (nextTab === 'posts' || nextTab === 'members') {
            updated.delete('file')
            updated.delete('event')
        } else if (nextTab === 'new-post') {
            updated.delete('file')
        } else if (nextTab === 'media') {
            updated.delete('event')
        } else if (nextTab === 'events') {
            updated.delete('file')
        }

        if (updated.toString() !== searchParamsString) {
            setSearchParams(updated, { replace: true })
        }
    }

    const onScroll = () => {
        if (scrollContainer.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainer.current

            if (Math.ceil(scrollTop + clientHeight + 1) >= scrollHeight && hasNextPage && !postStatus && !loadingPost) {
                setLoadingPost(true)
                const controller = new AbortController()
                const { signal } = controller
                getRoomPostsRequest(parseInt(roomId!), pageNum + 1)
                    .then(response => {
                        if (response.status === 200) {
                            dispatch(setRoomPostsData([...posts, ...response.data.results]))
                            setHasNextPage(Boolean(response.data.next))
                            setLoadingPost(false)
                            setPageNum(pageNum + 1)
                        }
                    })
                    .catch(error => {
                        setPageNum(1)
                        if (signal.aborted) return
                        console.log(error)
                    })
                return () => {
                    setPageNum(1)
                    controller.abort()
                }
            }
        }
    }

    return (
        <BackgroundWrapper>
            <Header roomHeader={true} />
            <div className="room-screen">
                {isLoading ? (
                    <LoadingAnimation></LoadingAnimation>
                ) :
                    (
                        <div className="room-layout">
                            <RoomInformation isMemberInRoom={isMemberInRoom} hasPrivelegedPermission={hasPrivelegedPermission} />
                            <div className="room-layout__right-panel">
                                <div
                                    className={`room-layout__right-panel-header ${(isMemberInRoom === JoinStatus.NON_MEMBER || JoinStatus.PENDING) && "room-layout__right-panel-header--join-button"
                                        }`}
                                >
                                    <RoomHeader
                                        roomType={capitalizeFirstLetter(roomDataState?.type! === RoomType.Private ? translation.private : translation.public)}
                                    />
                                </div>

                                <Tabs
                                    className="tabs-container"
                                    selectedIndex={tabIndex}
                                    onSelect={(index, lastIndex, event) => {
                                        HapticFeedback.selectionChanged();

                                        // Scroll to top if tapping same tab
                                        handleScrollToTop(index);

                                        updateSearchParamsForTab(index)
                                    }}
                                >

                                    <TabPanel>
                                        <div
                                            className="room-layout__right-panel-content"
                                            ref={(el) => {
                                                if (scrollContainer.current !== el) {
                                                    scrollContainer.current = el;
                                                }
                                                registerScrollContainer(0, el);
                                            }}
                                            onScroll={onScroll}
                                        >
                                            {roomDataState.type && !canSeePosts ? (
                                                <div className="room-layout__no-access">{translation.cantSeeRoomContent}</div>
                                            ) : posts!.length > 0 ? (
                                                posts?.map((post, index) => {
                                                    return (
                                                        <RoomPost
                                                            key={post.id}
                                                            showCommentIcon={true}
                                                            postDropdown={hasPostDropdown(post, role, organizationDataState.id!, userDataState.id, hasPrivelegedPermission)}
                                                            postDropdownStyle={
                                                                index === posts.length - 1
                                                                    ? "room-post__right-panel-content room-post__right-panel-content--no-height"
                                                                    : "room-post__right-panel-content room-post__right-panel-content--height-5"
                                                            }
                                                            noCursor={post.content_object === null ? true : false}
                                                            post={post}
                                                            postType={"posts"}
                                                            showFullPost={false}
                                                        />
                                                    )
                                                })
                                            ) : (
                                                <div className="room-layout__no-post">{translation.nopostsYet}</div>
                                            )}

                                            {loadingPost && <LoadingAnimation type="post" />}
                                        </div>
                                    </TabPanel>
                                    <TabPanel>

                                        <div className="room-layout__right-panel-content room-layout__right-panel-content--member-panel">
                                            <RoomMembers />
                                        </div>
                                    </TabPanel>
                                    <TabPanel>
                                        <RoomPostTab />
                                    </TabPanel>
                                    <TabPanel className={'media-library__tabs-panel'}>
                                        <MediaLibrary />
                                    </TabPanel>
                                    <TabPanel className={'room-events__tabs-panel'}>
                                        <RoomEvents />
                                    </TabPanel>
                                    {tabParam !== 'new-post' && (
                                        <button type="button" className="mobile-bottom-nav__fab" onClick={onFABClick} aria-label="Post erstellen">
                                            <PostsCommentsTabIcon />
                                        </button>
                                    )}
                                    <MobileBottomNav isRoomMenu={true} />
                                    <TabList>
                                        <Tab>
                                            <div className="react-tabs__tab-menu-item">
                                                <PostsTabIcon />
                                                <span>Austausch</span>
                                            </div>
                                        </Tab>
                                        <Tab>
                                            <div className="react-tabs__tab-menu-item">
                                                <MembersTabIcon />
                                                <span>Mitglieder</span>
                                            </div>
                                        </Tab>
                                        <Tab>
                                            <div className="react-tabs__tab-menu-item">
                                                <PostsCommentsTabIcon />
                                                <span>Posten</span>
                                            </div>
                                        </Tab>
                                        <Tab>
                                            <div className="react-tabs__tab-menu-item">
                                                <MediaTabIcon />
                                                <span>Mediathek</span>
                                            </div>
                                        </Tab>
                                        <Tab>
                                            <div className="react-tabs__tab-menu-item">
                                                <EventsTabIcon />
                                                <span>Events</span>
                                            </div>
                                        </Tab>
                                    </TabList>
                                </Tabs>
                            </div>
                        </div>
                    )}

            </div>
            <Footer roomfooter={true} />
            <ModalGroup />
        </BackgroundWrapper>
    )
}

export default RoomScreen
